import { createAction } from '@reduxjs/toolkit';
import mapKeys from 'lodash/mapKeys';
import {
  DIP_MARKETING_PREFERENCES,
  YOUR_MAIN_BANK_ACCOUNT,
  DIP_YOUR_MAIN_BANK_ACCOUNT,
  YOUR_EMPLOYMENT_DETAILS,
  POST_DIP_REVIEW_DETAIL
} from 'constants/paths';
import { responseFailed } from './error';
import { createAndUpdateCaseSuccessThunk } from './case';
import { showHideLoader } from './loader';
import { navigateToScreen } from './navigation';
import { pathOr } from 'ramda';
import { formatShortenedIsoDate } from 'utils/genericUtil';

const getCustomerById = createAction('customer/getCustomerById', function prepare(customerId) {
  return {
    payload: {
      endPoint: `/banks/:bankId/customers/${customerId}`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getCustomerByIdSuccess,
      failure: responseFailed,
      version: '1.0.0'
    }
  };
});

const getCustomerAndUpdateCase = createAction(
  'customer/getCustomerUpdateCase',
  function prepare({ customerId, caseId }) {
    return {
      payload: {
        endPoint: `/banks/:bankId/customers/${customerId}`,
        method: 'get',
        isPaasApi: true,
        addHeader: true,
        success: getCustomerAndUpdateCaseSuccessThunk,
        successParam: { reference_id: caseId },
        failure: responseFailed,
        version: '1.0.0'
      }
    };
  }
);

const getCustomerAndUpdateCaseSuccessThunk = (successResponse, reference_id) => (dispatch) => {
  dispatch(getCustomerByIdSuccess(successResponse));
  dispatch(createAndUpdateCaseSuccessThunk(reference_id));
};

const getCustomerByIdSuccess = createAction(
  'customer/getCustomerByIdSuccess',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const addEmploymentDetails = createAction(
  'customer/addEmploymentDetails',
  function prepare({ employmentJobLevel, employmentIndustry }) {
    return {
      payload: { employmentJobLevel, employmentIndustry }
    };
  }
);

const getCustomers = createAction('customer/getCustomers', function prepare() {
  return {
    payload: {
      endPoint: `/banks/:bankId/customers`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getCustomersSuccess,
      failure: responseFailed,
      version: '1.2.0'
    }
  };
});

const getCustomersSuccess = (response) => (dispatch) => {
  dispatch(getCustomersSuccessResponse(response));
  const customer = response.customers.find((customer) => customer.relationship === 'OWNER');

  if (customer) {
    dispatch(updateCustomerId(customer.id));
    dispatch(getCustomerById(customer.id));
  }
};

const getCustomersSuccessResponse = createAction(
  'customer/getCustomersSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

const updateCustomerId = createAction('customer/updateCustomerId', function prepare(customerId) {
  return {
    payload: { customerId }
  };
});

/**
 *
 * @param {*} customerInfo
 * @param {boolean} shouldNavigateToDIPOnGetCaseSuccess currently only true when navigating NTB customers from IncomeAndExpenses to AboutThisCheck
 * @returns
 */
const updateCustomerResponse =
  (customerInfo, shouldNavigateToDIPOnGetCaseSuccess = false) =>
  async (dispatch) => {
    await dispatch(updateCustomerInfo(customerInfo));
    await dispatch(getCustomerById(customerInfo.customerId));
    dispatch(showHideLoader(false));
    dispatch(
      navigateToScreen(
        shouldNavigateToDIPOnGetCaseSuccess ? POST_DIP_REVIEW_DETAIL : YOUR_EMPLOYMENT_DETAILS
      )
    );
  };

export const updateCustomerInfo = createAction(
  'updates/customerInfo',
  function prepare(customerInfo) {
    return {
      payload: customerInfo
    };
  }
);

export const updateDIPCustomerData = () => (dispatch, getState) => {
  const { config, customer, form, navigation, caseDetails } = getState();
  dispatch({
    type: 'dip-customer/update',
    payload: {
      endPoint: `banks/:bankId/customers/personal/${config.customerId}`,
      method: 'put',
      body: updateDIPCustomerRequest({ config, customer, form, caseDetails }),
      isPaasApi: true,
      addHeader: true,
      success: updateDIPCustomerSuccess,
      successParam: {
        navigation
      },
      failure: updateDIPCustomerFailed,
      version: '1.0.0'
    }
  });
};

const updateDIPCustomerRequest = ({ config, customer, form, caseDetails }) => {
  let {
    PersonalDetailsDIPForm: {
      values: {
        name: { title, first_name, middle_name, last_name } = {},
        marital_status,
        gender,
        number_of_dependents,
        marital_status_other
      } = {}
    } = {},
    AddressDetailsDIPForm: {
      values: {
        ADDRESS_HISTORY,
        nationality,
        LIVING_ARRANGEMENT,
        UKCitizenship,
        otherCountry,
        anotherCountry,
        MOVE_IN_DATE: { month, year } = {}
      } = {}
    } = {},
    EmploymentDetailsDIPForm: {
      values: {
        employmentStatus,
        otherEmploymentStatus,
        employmentStartDate,
        annualIncomeBeforeTax,
        monthlyIncome,
        paymentFrequency,
        mortgageExpense,
        creditExpense,
        employersName,
        occupation
      } = {}
    } = {}
  } = form;
  const { ntbToDipForm } = caseDetails;
  if (!form.PersonalDetailsDIPForm) {
    ({
      name: { title, first_name, middle_name, last_name } = {},
      marital_status,
      gender,
      number_of_dependents,
      marital_status_other
    } = ntbToDipForm.PersonalDetailsDIPForm.values);
  }
  if (!form.AddressDetailsDIPForm) {
    ({
      ADDRESS_HISTORY,
      nationality,
      LIVING_ARRANGEMENT,
      UKCitizenship,
      otherCountry,
      anotherCountry,
      MOVE_IN_DATE: { month, year } = {}
    } = ntbToDipForm.AddressDetailsDIPForm.values);
  }
  if (!form.EmploymentDetailsDIPForm) {
    ({
      employmentStatus,
      otherEmploymentStatus,
      employmentStartDate,
      annualIncomeBeforeTax,
      monthlyIncome,
      paymentFrequency,
      mortgageExpense,
      creditExpense,
      employersName,
      occupation
    } = ntbToDipForm.EmploymentDetailsDIPForm.values);
  }
  const {
    personal_details: { country_of_birth, ni_number, city_of_birth },
    financial_details: { tax },
    contact_details: { email, phone, preferences }
  } = customer.customerInfo;

  const getContactDetails = () => {
    if (form.ContactDetailsDIPForm) {
      const {
        ContactDetailsDIPForm: {
          values: { email_id, contact_preference }
        }
      } = form;
      return {
        email: email_id,
        phone,
        preferred_contact: contact_preference
      };
    }
    return {
      email,
      phone,
      // We have been asked by business to default this value to EMAIL in case the contact method is returned empty from RM
      preferred_contact: preferences?.contact?.method || 'EMAIL'
    };
  };
  const derivedAddressToList = [];
  const derivedAddressHistory = [];
  if (ADDRESS_HISTORY.length > 0) {
    ADDRESS_HISTORY.forEach((address, index) => {
      const addressShallowCopy = JSON.parse(JSON.stringify(address));
      const { effective_period } = addressShallowCopy;
      derivedAddressToList.push(
        index === 0
          ? `${year}-${month}-01`
          : formatShortenedIsoDate(pathOr('', ['from'], effective_period))
      );
      if (effective_period) {
        if (index === 0) {
          effective_period.from = `${year}-${month}-01`;
        } else {
          effective_period.from = formatShortenedIsoDate(effective_period.from);
          effective_period.to = formatShortenedIsoDate(derivedAddressToList[index - 1]);
        }
      }
      if (addressShallowCopy.uk_address) {
        addressShallowCopy.uk_address = mapKeys(addressShallowCopy.uk_address, (value, key) => {
          switch (key) {
            case 'street':
              return 'street_name';
            case 'address_line1':
              return 'address_line_1';
            case 'address_line2':
              return 'address_line_2';
            default:
              return key;
          }
        });
        if (addressShallowCopy?.uk_address?.address_reference) {
          delete addressShallowCopy.uk_address.address_reference;
        }
        if (addressShallowCopy?.uk_address?.address_line3) {
          delete addressShallowCopy.uk_address.address_line3;
        }
        if (addressShallowCopy?.uk_address?.address_line4) {
          delete addressShallowCopy.uk_address.address_line4;
        }
      }
      if (addressShallowCopy.international_address) {
        addressShallowCopy.international_address = mapKeys(
          addressShallowCopy.international_address,
          (value, key) => {
            switch (key) {
              case 'address_line1':
                return 'address_line_1';
              case 'address_line2':
                return 'address_line_2';
              case 'address_line3':
                return 'address_line_3';
              case 'address_line4':
                return 'address_line_4';
              default:
                return key;
            }
          }
        );
      }
      derivedAddressHistory.push(addressShallowCopy);
    });
  }
  const derivedCountries = [
    ...(otherCountry !== '' && otherCountry !== 'UK only' ? [otherCountry] : []),
    ...(anotherCountry !== '' && anotherCountry !== 'No' ? [anotherCountry] : [])
  ];
  return {
    applicants: [
      {
        id: config.customerId,
        personal_details: {
          name: { title, first_name, middle_name, last_name },
          country_of_birth,
          marital_status: marital_status === 'OTHER' ? marital_status_other : marital_status,
          nationality,
          number_of_dependents: number_of_dependents === '3+' ? '3' : number_of_dependents,
          city_of_birth,
          ni_number,
          residential_status: LIVING_ARRANGEMENT,
          sex: gender === 'Male' ? 'M' : 'F',
          addresses: derivedAddressHistory
        },
        contact_details: getContactDetails(),
        employment_details: [
          employmentStatus === 'OTHER' && otherEmploymentStatus !== 'SELF EMPLOYED'
            ? { status: otherEmploymentStatus }
            : {
                status: employmentStatus === 'OTHER' ? otherEmploymentStatus : employmentStatus,
                employer: { name: employersName },
                effective_period: { from: employmentStartDate ? employmentStartDate : '' },
                description: occupation
              }
        ],
        financial_details: {
          income: {
            gross_annual: parseFloat(annualIncomeBeforeTax),
            regular: {
              net_amount: parseFloat(monthlyIncome),
              frequency: ntbToDipForm ? 'ANNUAL' : paymentFrequency,
              input_type: ntbToDipForm ? 'HOUSEHOLD INCOME' : 'NET MONTHLY INCOME'
            }
          },
          expenditure: {
            mortgage: {
              net_amount: parseFloat(mortgageExpense),
              frequency: 'MONTHLY'
            },
            others: {
              net_amount: parseFloat(creditExpense),
              frequency: 'MONTHLY'
            }
          },
          tax: {
            ...tax,
            countries_of_citizenship: derivedCountries,
            uk_citizen: UKCitizenship === 'Yes' ? 'Y' : 'N'
          }
        }
      }
    ]
  };
};

export const updateDIPCustomerSuccess = (response) => (dispatch) => {
  dispatch({
    type: 'dip-customer/updateDIPCustomerSuccess',
    payload: response
  });
  dispatch(showHideLoader(false));
};

export const updateDIPCustomerFailed = (failedResponse) => (dispatch) => {
  const {
    originalError: {
      response: { data }
    }
  } = failedResponse.error;
  dispatch(showHideLoader(false));
  dispatch(responseFailed(data));
};

export const getPreferences = () => (dispatch) => {
  dispatch(showHideLoader(true));
  dispatch({
    type: 'dip-customer/getPreferences',
    payload: {
      endPoint: `/user/preferences`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getPreferencesSuccess,
      failure: responseFailed,
      version: '1.0.0'
    }
  });
};

export const getPreferencesResumeSuccess = (response) => (dispatch) => {
  dispatch(showHideLoader(false));
  dispatch({
    type: 'dip-customer/getPreferencesSuccess',
    payload: response
  });
};

export const getPreferencesResume = () => (dispatch) => {
  dispatch(showHideLoader(true));
  dispatch({
    type: 'dip-customer/getPreferences',
    payload: {
      endPoint: `/user/preferences`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getPreferencesResumeSuccess,
      failure: responseFailed,
      version: '1.0.0'
    }
  });
};

export const getPreferencesSuccess = (response) => (dispatch) => {
  dispatch(showHideLoader(false));
  dispatch({
    type: 'dip-customer/getPreferencesSuccess',
    payload: response
  });
  dispatch(navigateToScreen(DIP_MARKETING_PREFERENCES));
};

export const updateDIPMarketingPreferences = () => (dispatch, getState) => {
  const { config, customer, form } = getState();
  dispatch({
    type: 'dip-customer/update-marketing-preferences',
    payload: {
      endPoint: `/user/preferences`,
      method: 'put',
      body: updateDIPMarketingPreferencesRequest({ form }),
      isPaasApi: true,
      addHeader: true,
      success: updateDIPMarketingPreferencesRequestSuccess,
      failure: responseFailed,
      version: '1.0.0'
    }
  });
};

const updateDIPMarketingPreferencesRequest = ({ form }) => {
  const {
    MarketingPreferencesDIPForm: {
      values: { email_pref, internet_mobile_banking_pref, sms_pref, phone_pref, post_pref }
    }
  } = form;
  return {
    marketing_preferences: [
      { type: 'EMAIL', value: email_pref },
      { type: 'INTERNET_BANKING', value: internet_mobile_banking_pref },
      { type: 'MESSAGING', value: sms_pref },
      { type: 'VOICE', value: phone_pref },
      { type: 'POST', value: post_pref }
    ]
  };
};

export const displayMarketingPreferenceModal = createAction(
  'dip/display-marketing-preference-modal',
  function prepare(showHideModal) {
    return {
      payload: showHideModal
    };
  }
);

export const updateDIPMarketingPreferencesRequestSuccess = () => (dispatch, getState) => {
  const { config } = getState();
  dispatch(showHideLoader(false));
  dispatch(displayMarketingPreferenceModal(true));
  setTimeout(() => {
    dispatch(
      navigateToScreen(
        config.skipDecisionInPrinciple ? YOUR_MAIN_BANK_ACCOUNT : DIP_YOUR_MAIN_BANK_ACCOUNT
      )
    );
    dispatch(displayMarketingPreferenceModal(false));
  }, 2000);
};

export const setAddressFieldErrors = createAction('customer/setAddressFieldErrors');

export const clearAddressFieldErrors = createAction('customer/clearAddressFieldErrors');

export const setAddressValidation = createAction(
  'customer/setAddressValidation',
  function prepare(errors) {
    return {
      payload: errors
    };
  }
);

export const dropInIncome = createAction('loanValues/dropInIncome', function prepare(newIncome) {
  return {
    payload: newIncome
  };
});

export const setFinancialCircumstanceValue = createAction(
  'customer/setFinancialCircumstanceValue',
  function prepare(value) {
    return {
      payload: value
    };
  }
);

export const clearNewIncome = createAction('loanValues/clearNewIncome');

export {
  getCustomerById,
  getCustomerByIdSuccess,
  getCustomers,
  getCustomersSuccessResponse,
  updateCustomerId,
  updateCustomerResponse,
  getCustomerAndUpdateCase
};
